import React, { useEffect, Suspense, lazy } from "react";
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { createStateSubscription, destroyStateSubscription, updateStateSubject } from "@gartner/genie-messenger-util";
import Spinner from "@genie-ui/react/v2/Spinner";
import { Constants } from "@constants/Constants";
import { getGCOMEnv, getCurrentPageForActis } from "@utils/utilities";
import { login } from "./lib/auth";
import { authStart, authSuccess, authFailure } from "@store/slices/auth.slice";
import { ActisReporting, ACTIS_REPORTING } from "@utils/actisReporting";
import TabsNavigation from "@components/organisms/TabsNavigation";
import PageTitle from "@components/atoms/PageTitle/PageTitle";
import { queryStringToObject } from "@utils/utilities";
import { useIntercom } from "react-use-intercom";

const ErrorPage = lazy(() => import('@components/pages/ErrorPage'));
const UseCaseToaster = lazy(() => import('@components/atoms/UseCaseToaster/UseCaseToaster'));

import "./app.scss";

const GoToGCOM = () => {
  window.location.href = getGCOMEnv();
};

// 1. Define a Root Layout that holds persistent navigation elements
const RootLayout = ({ title }) => {
  return (
    <>
      <PageTitle title={title} />
      <TabsNavigation />
      {/* Outlet renders child routes if any */}
      <Outlet />
      <UseCaseToaster />
    </>
  );
};

// 2. Define router OUTSIDE the component so it is not re-created on every render
const router = createBrowserRouter([
  {
    path: `${Constants.ROUTES.BASE_URL}`,
    element: <RootLayout title={Constants.ROUTES_BROWSER_TITLE.HOME} />,
  },
  {
    path: `${Constants.ROUTES.BASE_URL}${Constants.ROUTES.USE_CASES}`,
    element: <RootLayout title={Constants.ROUTES_BROWSER_TITLE.USE_CASE} />,
  },
  {
    path: `${Constants.ROUTES.BASE_URL}${Constants.ROUTES.DASHBOARD}`,
    element: <RootLayout title={Constants.ROUTES_BROWSER_TITLE.DASHBOARD} />,
  },
  {
    path: `${Constants.ROUTES.BASE_URL}${Constants.ROUTES.SCORECARD}`,
    element: <RootLayout title={Constants.ROUTES_BROWSER_TITLE.SCORECARD} />,
  },
  {
    path: `${Constants.ROUTES.BASE_URL}${Constants.ROUTES.SCORECARD}${Constants.ROUTES.IMPORT_USE_CASES}`,
    element: <RootLayout title={Constants.ROUTES_BROWSER_TITLE.IMPORT_USE_CASES} />,
  },
  {
    path: `${Constants.ROUTES.BASE_URL}${Constants.ROUTES.GARTNER_ASSESSMENTS}`,
    element: <RootLayout title={Constants.ROUTES_BROWSER_TITLE.GARTNER_ASSESSMENTS} />,
  },
  {
    path: `${Constants.ROUTES.BASE_URL}${Constants.ROUTES.CASE_EXAMPLES}`,
    element: <RootLayout title={Constants.ROUTES_BROWSER_TITLE.CASE_EXAMPLES} />,
  },
  {
    path: `${Constants.ROUTES.BASE_URL}${Constants.ROUTES.USE_CASES}${Constants.ROUTES.DETAIL}${Constants.ROUTES.SLUG_PARAM}`,
    element: <RootLayout />, // Dynamic page title should be handled inside the target view/component
  },
  {
    path: `${Constants.ROUTES.BASE_URL}${Constants.ROUTES.CASE_EXAMPLES}${Constants.ROUTES.DETAIL}${Constants.ROUTES.SLUG_PARAM}`,
    element: <RootLayout />, // Dynamic page title should be handled inside the target view/component
  },
  {
    path: `${Constants.ROUTES.BASE_URL}${Constants.ROUTES.ACCESS_DENIED}`,
    element: (
      <ErrorPage
        title={Constants.ACCESS_DENIED_ERROR.TITLE}
        body1={Constants.ACCESS_DENIED_ERROR.BODY1}
        body2={Constants.ACCESS_DENIED_ERROR.BODY2}
        label={Constants.ACCESS_DENIED_ERROR.LABEL}
        handleClick={GoToGCOM}
      />
    ),
  },
  {
    path: `*`,
    element: <ErrorPage body1={Constants.PAGE_NOT_FOUND_ERROR.TITLE} />,
  },
]);

const App = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const { loading, error, user } = auth;
  ActisReporting.prototype.binder = { ...auth.user };
  const { ROUTES: { ACCESS_DENIED } } = Constants;
  const { boot: bootIntercom, shutdown: shutdownIntercom } = useIntercom();

  const checkRefValueForActisUpdate = (currRefValue) => {
    const { 
      HAM_MENU_REF, HP_DF_REF, SHORTCUT_NAV_REF,
      GCOM_SEARCH_POPULAR_REF, MY_TOOLS_REF, 
      AI_MICROSITE_REF, TOOL_WRAPPER_REF,
    } = Constants.EXTERNAL_ACTIS_UPDATE_REF;
    const refValueInUpperCase = currRefValue?.toUpperCase();

    return (
      refValueInUpperCase === HAM_MENU_REF || refValueInUpperCase === HP_DF_REF ||
      refValueInUpperCase === SHORTCUT_NAV_REF || refValueInUpperCase === GCOM_SEARCH_POPULAR_REF ||
      refValueInUpperCase === MY_TOOLS_REF || refValueInUpperCase === AI_MICROSITE_REF ||
      refValueInUpperCase === TOOL_WRAPPER_REF
    );
  };

  const actisForGCOMReference = () => {
    const url = new URL(window.location.href);
    const currRefValue = queryStringToObject(url.search).ref;

    if (url.searchParams.has("ref") && checkRefValueForActisUpdate(currRefValue)) {
      ACTIS_REPORTING[currRefValue]?.push({
        action: Constants?.ACTIS.EXTERNAL_ACTIS[currRefValue]?.ACTION,
        label: Constants?.ACTIS.EXTERNAL_ACTIS[currRefValue]?.LABEL.replace(
          Constants.ACTIS.CURRENT_PAGE_REPLACE, getCurrentPageForActis()
        ),
      });
      url.searchParams.delete("ref");
      window.history.pushState({}, "", url);
    }
  };

  useEffect(() => {
    const subscriptionLoginDetails = createStateSubscription(
      "login",
      (data) => {
        data?.userId ? dispatch(authSuccess(data)) : dispatch(authFailure());  
        ACTIS_REPORTING.LOGIN.push({
          action: Constants.ACTIS.LOGIN.ACTION,
          label: Constants.ACTIS.LOGIN.LABEL.replace(
            Constants.ACTIS.CURRENT_PAGE_REPLACE, getCurrentPageForActis()
          ),
        });
        setTimeout(() => {
          bootIntercom({
            email: data.email,
            name: `${data.firstName}`,
            customAttributes: {
              gartner_user: data?.email?.endsWith('gartner.com'),
              job_title: data?.title,
              roles: data?.roles?.join(','),
              actis_id: data?.userId,
            },
            intercomUserJwt: data.intercomJwtToken,
          });
        }, Constants.INTERCOM.TIMEOUT);
        actisForGCOMReference();
      }
    );

    const subscriptionLogoutDetails = createStateSubscription(
      "logout",
      () => {
        shutdownIntercom();
      }
    );

    if (window.location.href.indexOf(ACCESS_DENIED) > -1 && user?.userId) {
      updateStateSubject("login", {});
    } else if (!user?.userId) {
      dispatch(authStart());
      login();
    }

    const secondaryLogoSubscription = createStateSubscription(
      'secondaryLogoClick',
      (isLogoClick) => {
        if (isLogoClick) {
          updateStateSubject('secondaryLogoClick', false);
        }
      },
    );

    const shutdownIntercomHandlerForStorageChange = (event) => {
      if (event.key === Constants.LOGOUT_EVENT_KEY && event.newValue === 'true') {
        shutdownIntercom();
      }
    };
    window.addEventListener('storage', shutdownIntercomHandlerForStorageChange);

    return () => {
      destroyStateSubscription(subscriptionLoginDetails);
      destroyStateSubscription(subscriptionLogoutDetails);
      destroyStateSubscription(secondaryLogoSubscription);
      window.removeEventListener('storage', shutdownIntercomHandlerForStorageChange);
    };
  }, []);

  if (loading && !error) {
    return (
      <div className="loader">
        <Spinner />
      </div>
    );
  }

  if (error && !loading) {
    return <ErrorPage />;
  }

  return (
    <Suspense fallback={<div className="mercury-fallbackSpinner"><Spinner /></div>}>
      <RouterProvider router={router} />
    </Suspense>
  );
};

export default App;